import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { ContentCard, Leaderboard } from '@/components/customComponents'

import {
  barChartOptions,
  lineChartOptions,
  pieChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import { useSales, useTemperatureSizeSales } from '@/hooks'
import useSalesPersona from '@/hooks/clarityLens/salesLens/sales/useSalesPersona'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxSalesLensFilter,
} from '@/store'
import { SeasonalityModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { useEffect } from 'react'

interface SalesTabProps {
  itemName: string
}

export const SalesTab = ({ itemName }: SalesTabProps) => {
  const { t } = useTranslation()
  const { selectedBranch, date, sizeFilter, temperatureFilter } =
    useReduxSalesLensFilter()
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('sales-lens-item')
  )

  const {
    timeOfDayBreakdownChartData,
    storeBreakdownChartData,
    seasonalityChartData,
    loading: salesLoading,
    refetch: salesRefetch,
  } = useSales(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName,
    sizeFilter,
    temperatureFilter
  )

  const {
    salesByStyleChartData,
    salesBySizeChartData,
    loading: temperatureSizeSalesLoading,
    refetch: temperatureSizeSalesRefetch,
  } = useTemperatureSizeSales(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  const {
    salesPersonaData,
    SALES_PERSONA_COLUMNS,
    loading: salesPersonaLoading,
    refetch: salesPersonaRefetch,
  } = useSalesPersona(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing sales data...')
      salesRefetch()
      temperatureSizeSalesRefetch()
      salesPersonaRefetch()
    }
  }, [refreshTrigger, salesRefetch])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ContentCard
          title={t('clarityLens.salesLens.charts.timeOfDayBreakdown')}
          loading={salesLoading}
          isEmpty={!timeOfDayBreakdownChartData}
        >
          {timeOfDayBreakdownChartData && (
            <ReactEcharts
              option={getStackedBarChartData(timeOfDayBreakdownChartData)}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.storeBreakdown')}
          loading={salesLoading}
          isEmpty={!storeBreakdownChartData?.data}
        >
          {storeBreakdownChartData && (
            <ReactEcharts
              option={getBarChartData(storeBreakdownChartData.data)}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <ContentCard
          title={t('clarityLens.salesLens.charts.seasonality')}
          loading={salesLoading}
          isEmpty={!seasonalityChartData?.data}
        >
          {seasonalityChartData && (
            <ReactEcharts
              option={getLineChartData(seasonalityChartData.data)}
            />
          )}
        </ContentCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* <DetailedNotificationBox
          hierarchy={{
            clarityLens: clarityLensType,
            item: itemName,
          }}
        /> */}
        <ContentCard
          title={t('clarityLens.salesLens.charts.salesBySize')}
          loading={temperatureSizeSalesLoading}
          isEmpty={
            !salesBySizeChartData ||
            Object.keys(salesBySizeChartData.data || {}).length === 0
          }
        >
          {salesBySizeChartData && (
            <ReactEcharts
              option={getPieChartData(
                salesBySizeChartData?.data,
                salesBySizeChartData?.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.salesByTemperature')}
          loading={temperatureSizeSalesLoading}
          isEmpty={
            !salesByStyleChartData ||
            Object.keys(salesByStyleChartData.data || {}).length === 0
          }
        >
          {salesByStyleChartData && (
            <ReactEcharts
              option={getPieChartData(
                salesByStyleChartData.data,
                salesByStyleChartData.labels
              )}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <Leaderboard
          title={t('clarityLens.salesLens.charts.personaList')}
          data={salesPersonaData}
          columns={SALES_PERSONA_COLUMNS}
          loading={salesPersonaLoading}
          enablePagination
          itemsPerPage={5}
        />
      </div>
    </div>
  )
}

const getStackedBarChartData = (primaryData: {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}) => {
  return stackedBarChartOptions(
    primaryData.rawData,
    primaryData.seriesNames,
    primaryData.xAxisNames,
    '40%',
    'money'
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  return pieChartOptions(data, labels, 'money')
}

const getBarChartData = (
  storeData: { branchName: string; grossSalesAmount: number }[]
) => {
  const rawData = [storeData.map((item) => item.grossSalesAmount)]
  const seriesNames = ['Sales']
  const xAxisNames = storeData.map((item) => item.branchName)

  return barChartOptions(rawData, seriesNames, xAxisNames, 'percentage')
}

const getLineChartData = (seasonalityData: SeasonalityModel) => {
  // Safety check for empty data
  if (!seasonalityData || Object.keys(seasonalityData).length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  const periods = Object.keys(seasonalityData) // These will be our line names/series
  const allMonths = new Set<string>()

  // Collect all unique months (these will be our X-axis data points)
  periods.forEach((period) => {
    if (seasonalityData[period] && Array.isArray(seasonalityData[period])) {
      seasonalityData[period].forEach((item) => {
        if (item && item.month) {
          allMonths.add(item.month)
        }
      })
    }
  })

  const monthNames = Array.from(allMonths) // Months should be in their natural order

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  // Create raw data for each period (line)
  const rawData = periods.map((period) => {
    return monthNames.map((month) => {
      const monthData = seasonalityData[period]?.find(
        (item) => item?.month === month
      )
      return monthData ? monthData.grossSalesAmount : 0
    })
  })

  return lineChartOptions(rawData, periods, monthNames, 'money')
}
